import { create } from 'zustand';
import {
  ContextSnippet,
  CreateContextSnippetPayload,
  UpdateContextSnippetPayload,
  PreviewContextSnippetPayload,
  PreviewContextSnippetResponse,
} from '@/types/api';
import {
  getContextSnippets as apiGetContextSnippets,
  createContextSnippet as apiCreateContextSnippet,
  updateContextSnippet as apiUpdateContextSnippet,
  deleteContextSnippet as apiDeleteContextSnippet,
  previewContextSnippet as apiPreviewContextSnippet,
} from '@/lib/api';
import { toast } from 'sonner';

interface ContextSnippetState {
  snippets: ContextSnippet[];
  isLoading: boolean;
  isInitialized: boolean;
  error: string | null;
  previewResult: PreviewContextSnippetResponse | null;
  editingSnippetId: string | 'new' | null;
  setEditingSnippetId: (id: string | 'new' | null) => void;
  fetchSnippets: () => Promise<void>;
  createSnippet: (
    payload: CreateContextSnippetPayload,
  ) => Promise<ContextSnippet | null>;
  updateSnippet: (
    id: string,
    payload: UpdateContextSnippetPayload,
  ) => Promise<ContextSnippet | null>;
  deleteSnippet: (id: string) => Promise<void>;
  previewSnippet: (payload: PreviewContextSnippetPayload) => Promise<void>;
  clearPreview: () => void;
}

export const useContextSnippetStore = create<ContextSnippetState>((set, get) => ({
  snippets: [],
  isLoading: false,
  isInitialized: false,
  error: null,
  previewResult: null,
  editingSnippetId: null,
  setEditingSnippetId: (id) => set({ editingSnippetId: id }),

  fetchSnippets: async () => {
    // Skip if already initialized or currently loading (prevents duplicate fetch in Strict Mode)
    const { isInitialized, isLoading } = get();
    if (isInitialized || isLoading) return;
    
    set({ isLoading: true, error: null });
    try {
      const snippets = await apiGetContextSnippets();
      set({ snippets, isLoading: false, isInitialized: true });
    } catch (err) {
      const errorMsg =
        err instanceof Error ? err.message : 'Failed to fetch context snippets.';
      set({ error: errorMsg, isLoading: false });
      toast.error(errorMsg);
    }
  },

  createSnippet: async (payload) => {
    set({ isLoading: true, error: null });
    try {
      const newSnippet = await apiCreateContextSnippet(payload);
      set((state) => ({
        snippets: [...state.snippets, newSnippet].sort((a, b) =>
          a.handle.localeCompare(b.handle),
        ),
        isLoading: false,
      }));
      toast.success('Context snippet created successfully.');
      return newSnippet;
    } catch (err) {
      const errorMsg =
        err instanceof Error ? err.message : 'Failed to create context snippet.';
      set({ error: errorMsg, isLoading: false });
      toast.error(errorMsg);
      return null;
    }
  },

  updateSnippet: async (id, payload) => {
    set({ isLoading: true, error: null });
    try {
      const updatedSnippet = await apiUpdateContextSnippet(id, payload);
      set((state) => ({
        snippets: state.snippets
          .map((s) => (s.id === id ? updatedSnippet : s))
          .sort((a, b) => a.handle.localeCompare(b.handle)),
        isLoading: false,
      }));
      toast.success('Context snippet updated successfully.');
      return updatedSnippet;
    } catch (err) {
      const errorMsg =
        err instanceof Error ? err.message : 'Failed to update context snippet.';
      set({ error: errorMsg, isLoading: false });
      toast.error(errorMsg);
      return null;
    }
  },

  deleteSnippet: async (id: string) => {
    const originalSnippets = get().snippets;
    set((state) => ({
      snippets: state.snippets.filter((s) => s.id !== id),
      isLoading: true,
    }));
    try {
      await apiDeleteContextSnippet(id);
      toast.success('Context snippet deleted successfully.');
      set({ isLoading: false });
    } catch (err) {
      const errorMsg =
        err instanceof Error ? err.message : 'Failed to delete context snippet.';
      set({ error: errorMsg, snippets: originalSnippets, isLoading: false });
      toast.error(errorMsg);
    }
  },

  previewSnippet: async (payload: PreviewContextSnippetPayload) => {
    set({ isLoading: true, error: null, previewResult: null });
    try {
      const result = await apiPreviewContextSnippet(payload);
      set({ previewResult: result, isLoading: false });
      if (result.error) {
        toast.error(`Preview failed: ${result.error}`);
      } else {
        toast.success('Snippet preview generated successfully.');
      }
    } catch (err) {
      const errorMsg =
        err instanceof Error ? err.message : 'Failed to generate preview.';
      set({
        error: errorMsg,
        isLoading: false,
        previewResult: {
          rendered_text: '',
          error: errorMsg,
        },
      });
      toast.error(errorMsg);
    }
  },

  clearPreview: () => {
    set({ previewResult: null });
  },
}));